/**
 * Step definitions for the purchase wizard (WI-TV-W6-001).
 *
 * WI-TV-W6-001 delivered the locked purchase step ordering and step 1 —
 * home-search status and property details. WI-TV-W6-002 delivered the purchase
 * price, down payment, and derived loan amount. WI-TV-W6-003 replaces the last
 * placeholders with the real stated-credit, military-eligibility, and
 * primary-contact steps, which are the shared purpose-neutral `borrower-steps`
 * the refinance journey already uses — the same value keys, authoritative enums,
 * validation, Skip semantics, and exact LMD mappings, so the two journeys cannot
 * drift. Only the email step's follow-up sentence is purchase-specific.
 *
 * Value keys are the authoritative `ReferralFormState` field names wherever the
 * model already has one, so purchase answers map into the existing payload
 * builder without new naming or payload semantics. The single new key is the
 * borrower's home-search status, which the model already expresses as
 * `propertyStillLooking`.
 */
import { PROPERTY_TYPE_OPTIONS, STATE_OPTIONS, type SelectOption } from "../models/referral";
import { isValidStateCode } from "../form/validation";
import { createCoBorrowerSteps } from "./coborrower-steps";
import {
  ESTIMATED_VALUE_KEY,
  LOAN_AMOUNT_KEY,
  PROPERTY_CITY_KEY,
  PROPERTY_STATE_KEY,
  PROPERTY_STREET_KEY,
  PROPERTY_TYPE_KEY,
  PROPERTY_ZIP_KEY,
  PROPERTY_ZIP_REGEX,
} from "./refinance-steps";
import {
  bindWholeDollarMoneyInput,
  canonicalMoneyTextFromInput,
  formatWholeDollarForDisplay,
  parseMoneyInput,
  subtractMoneyText,
  WHOLE_DOLLAR_FORMAT_MESSAGE,
} from "./money";
import {
  bindRadioGroup,
  bindValueInputs,
  radioGroupHtml,
  selectFieldHtml,
  textFieldHtml,
} from "./step-fields";
import {
  borrowerNameStep,
  borrowerPhoneStep,
  createBorrowerEmailStep,
  creditStep,
  militaryStep,
} from "./borrower-steps";
import type { WizardFieldError, WizardStep, WizardValues } from "./wizard-machine";

/**
 * The borrower's down payment. It derives the existing required `loanAmount` and
 * is carried through the purchase submission for LeadIntake.
 */
export const DOWN_PAYMENT_KEY = "downPayment";

/** Whether the borrower has found a property yet. Required on step 1. */
export const HOME_SEARCH_STATUS_KEY = "homeSearchStatus";

/** The borrower found a property; manual address entry is offered. */
export const HOME_SEARCH_FOUND = "found";

/** The borrower is still looking; there is no address to collect. */
export const HOME_SEARCH_LOOKING = "looking";

export const HOME_SEARCH_OPTIONS: readonly SelectOption[] = [
  { value: HOME_SEARCH_FOUND, label: "I found a property" },
  { value: HOME_SEARCH_LOOKING, label: "I'm still looking" },
];

/** The manual address fields shown only once a property has been found. */
export const MANUAL_ADDRESS_KEYS = [
  PROPERTY_STREET_KEY,
  PROPERTY_CITY_KEY,
  PROPERTY_ZIP_KEY,
] as const;

/** True when the stored status is the borrower's affirmative found answer. */
export function hasFoundProperty(values: WizardValues): boolean {
  return values.get(HOME_SEARCH_STATUS_KEY) === HOME_SEARCH_FOUND;
}

/**
 * Step 1 — Home-search status and property details (cadence per WI-TV-W65-002).
 *
 * The home-search choice, the property type, and the property state are all
 * required, so the step can never be bypassed. Property type is the first
 * property control on the step. Answering "I found a property" reveals a visibly
 * separated, bordered address group ordered street, city, state, ZIP; the
 * street, city, and ZIP fields stay optional and a supplied ZIP must be exactly
 * five digits, matching the authoritative `propertyZipCode` rule. Answering
 * "I'm still looking" shows the required property state alone with no address
 * group, and clears street, city, and ZIP immediately — the state answer is kept
 * so switching back does not force a re-pick — so an address the borrower can no
 * longer see can never be carried into the submitted payload. DOM order,
 * validation order, focus order, and visual order agree in both branches.
 * Address entry is manual only: there is no lookup, suggestion source, or
 * external request anywhere on this step.
 */
export const homeSearchStep: WizardStep = {
  id: "home-search",
  title: "Your home search",
  validate: (values: WizardValues): WizardFieldError[] => {
    const errors: WizardFieldError[] = [];

    // Errors are produced in DOM order so the first one focuses the first
    // invalid control on the step.
    const status = values.get(HOME_SEARCH_STATUS_KEY).trim();
    if (!HOME_SEARCH_OPTIONS.some((o) => o.value === status)) {
      errors.push({
        field: `${HOME_SEARCH_STATUS_KEY}-${HOME_SEARCH_FOUND}`,
        message: "Tell us where you are in your home search to continue.",
      });
    }

    const propertyType = values.get(PROPERTY_TYPE_KEY).trim();
    if (!PROPERTY_TYPE_OPTIONS.some((o) => o.value === propertyType)) {
      errors.push({ field: PROPERTY_TYPE_KEY, message: "Select the property type to continue." });
    }

    const state = values.get(PROPERTY_STATE_KEY).trim();
    if (state === "") {
      errors.push({ field: PROPERTY_STATE_KEY, message: "Select the property state to continue." });
    } else if (!isValidStateCode(state)) {
      errors.push({
        field: PROPERTY_STATE_KEY,
        message: "Select a valid US state or territory for the property.",
      });
    }

    // The ZIP field only exists inside the found-property address group, after
    // the state control; a still-looking borrower has nothing to validate here.
    if (status === HOME_SEARCH_FOUND) {
      const zip = values.get(PROPERTY_ZIP_KEY).trim();
      if (zip !== "" && !PROPERTY_ZIP_REGEX.test(zip)) {
        errors.push({
          field: PROPERTY_ZIP_KEY,
          message: "Enter a 5-digit ZIP code, or leave it blank.",
        });
      }
    }

    return errors;
  },
  render: (container: HTMLElement, values: WizardValues): void => {
    const stateFieldHtml = (): string =>
      selectFieldHtml(
        PROPERTY_STATE_KEY,
        "Property state",
        "Select a property state",
        STATE_OPTIONS,
        values.get(PROPERTY_STATE_KEY),
      );

    const renderPropertyDetails = (): void => {
      const slot = container.querySelector<HTMLElement>("[data-property-details]");
      if (!slot) return;
      if (hasFoundProperty(values)) {
        // A visibly separated, bordered group ordered street, city, state, ZIP.
        slot.innerHTML = `
          <fieldset class="wizard-field-group" data-address-group>
            <legend class="wizard-field-group-legend">Property address</legend>
            <p class="wizard-step-desc">
              Add the address if you know it. Street, city, and ZIP are optional; the
              property state is required.
            </p>
            ${textFieldHtml(PROPERTY_STREET_KEY, "Property street address", values.get(PROPERTY_STREET_KEY))}
            ${textFieldHtml(PROPERTY_CITY_KEY, "Property city", values.get(PROPERTY_CITY_KEY))}
            ${stateFieldHtml()}
            ${textFieldHtml(PROPERTY_ZIP_KEY, "Property ZIP code", values.get(PROPERTY_ZIP_KEY), {
              inputmode: "numeric",
              maxlength: 5,
            })}
          </fieldset>`;
      } else {
        // Still looking: the required property state alone, no address group.
        slot.innerHTML = stateFieldHtml();
      }
      bindValueInputs(slot, values);
    };

    container.innerHTML = `
      <p class="wizard-step-desc" id="home-search-desc">
        Where are you in your home search?
      </p>
      ${radioGroupHtml(
        HOME_SEARCH_STATUS_KEY,
        "Home-search status",
        HOME_SEARCH_OPTIONS,
        values.get(HOME_SEARCH_STATUS_KEY),
        { autofocus: true, labelledBy: "home-search-desc" },
      )}
      ${selectFieldHtml(
        PROPERTY_TYPE_KEY,
        "Property type",
        "Select a property type",
        PROPERTY_TYPE_OPTIONS,
        values.get(PROPERTY_TYPE_KEY),
      )}
      <div data-property-details></div>`;

    bindValueInputs(container, values);
    bindRadioGroup(container, HOME_SEARCH_STATUS_KEY, values, (status) => {
      // Moving back to "still looking" discards the manual address immediately
      // rather than keeping a hidden answer alive; the state answer is retained.
      if (status !== HOME_SEARCH_FOUND) {
        for (const key of MANUAL_ADDRESS_KEYS) values.set(key, "");
      }
      renderPropertyDetails();
    });
    renderPropertyDetails();
  },
};

/**
 * Recomputes the derived loan amount from the current purchase price and down
 * payment, storing canonical text under the existing `loanAmount` key.
 *
 * The derivation is the single writer of that key on the purchase journey, so a
 * price or down-payment edit — including one made after backward navigation —
 * can never leave a stale requested-loan value behind. When the pair does not
 * derive a positive representable amount the key is cleared rather than left at
 * a previous answer's result.
 */
export function syncDerivedLoanAmount(values: WizardValues): string {
  const derived = subtractMoneyText(values.get(ESTIMATED_VALUE_KEY), values.get(DOWN_PAYMENT_KEY));
  values.set(LOAN_AMOUNT_KEY, derived ?? "");
  return derived ?? "";
}

/** The canonical purchase price, or `null` when it is not an accepted amount. */
function acceptedPurchasePrice(values: WizardValues): string | null {
  const price = canonicalMoneyTextFromInput(values.get(ESTIMATED_VALUE_KEY));
  if (price === null) return null;
  return parseMoneyInput(price) === 0 ? null : price;
}

/**
 * Binds a whole-dollar money control so only canonical text reaches the value
 * store while the display formats dynamically as the borrower types, pastes,
 * deletes, or edits. Manual entry is whole dollars only; the dollar sign and
 * grouping separators stay in the display layer and cannot change the stored
 * value's precision or magnitude. Decimal or unsupported input is rejected at
 * the edge without overwriting the accepted value, and a trusted decimal prefill
 * already in the store stays exact (displayed truncated) until the borrower's
 * first accepted manual edit replaces it — so the shared
 * {@link bindWholeDollarMoneyInput} primitive owns the caret, composition, and
 * blur behavior and both money steps behave identically.
 */
function bindMoneyInput(input: HTMLInputElement, key: string, values: WizardValues): void {
  bindWholeDollarMoneyInput(
    input,
    (canonical) => {
      values.set(key, canonical ?? "");
    },
    values.get(key),
  );
}

/**
 * Step 2 — Estimated purchase price (WI-TV-W6-002).
 *
 * Required, and accepted only as an exactly representable dollars-and-cents
 * amount greater than zero. The answer maps onto the existing `estimatedValue`
 * field, so nothing new reaches the wire.
 */
export const purchasePriceStep: WizardStep = {
  id: "purchase-price",
  title: "Estimated purchase price",
  validate: (values: WizardValues): WizardFieldError[] => {
    const raw = values.get(ESTIMATED_VALUE_KEY);
    if (raw.trim() === "") {
      return [
        { field: ESTIMATED_VALUE_KEY, message: "Enter the estimated purchase price to continue." },
      ];
    }
    const parsed = parseMoneyInput(raw);
    if (parsed === null)
      return [{ field: ESTIMATED_VALUE_KEY, message: WHOLE_DOLLAR_FORMAT_MESSAGE }];
    if (!(parsed > 0)) {
      return [{ field: ESTIMATED_VALUE_KEY, message: "Enter a purchase price greater than $0." }];
    }
    syncDerivedLoanAmount(values);
    return [];
  },
  render: (container: HTMLElement, values: WizardValues): void => {
    container.innerHTML = `
      <p class="wizard-step-desc">
        How much do you expect the home to cost? Your best estimate is fine.
      </p>
      ${textFieldHtml(
        ESTIMATED_VALUE_KEY,
        "Estimated purchase price",
        formatWholeDollarForDisplay(values.get(ESTIMATED_VALUE_KEY)),
        { required: true, inputmode: "numeric" },
      )}`;

    const input = container.querySelector<HTMLInputElement>(`#${ESTIMATED_VALUE_KEY}`);
    if (!input) return;
    bindMoneyInput(input, ESTIMATED_VALUE_KEY, values);
    // A changed price re-derives the loan amount immediately, so the value the
    // borrower reviews on the next step is never the previous price's result.
    input.addEventListener("input", () => syncDerivedLoanAmount(values));
    input.addEventListener("blur", () => syncDerivedLoanAmount(values));
  },
};

/**
 * Step 3 — Down payment and the derived estimated loan amount (WI-TV-W6-002).
 *
 * Required, at least zero, and strictly below the purchase price. The estimated
 * loan amount is derived from the two answers in integer cents and stored under
 * the existing `loanAmount` key while the down payment remains available for
 * LeadIntake submission.
 */
export const downPaymentStep: WizardStep = {
  id: "down-payment",
  title: "Down payment",
  validate: (values: WizardValues): WizardFieldError[] => {
    const raw = values.get(DOWN_PAYMENT_KEY);
    if (raw.trim() === "") {
      values.set(LOAN_AMOUNT_KEY, "");
      return [{ field: DOWN_PAYMENT_KEY, message: "Enter your down payment to continue." }];
    }

    const downPayment = canonicalMoneyTextFromInput(raw);
    if (downPayment === null) {
      values.set(LOAN_AMOUNT_KEY, "");
      return [{ field: DOWN_PAYMENT_KEY, message: WHOLE_DOLLAR_FORMAT_MESSAGE }];
    }

    const price = acceptedPurchasePrice(values);
    if (price === null) {
      values.set(LOAN_AMOUNT_KEY, "");
      return [
        {
          field: DOWN_PAYMENT_KEY,
          message: "Go back and enter an estimated purchase price before your down payment.",
        },
      ];
    }

    // The estimated loan amount is what the referral carries, so a pair that
    // cannot derive a positive representable amount blocks here rather than
    // submitting a rounded or zero loan.
    if (syncDerivedLoanAmount(values) === "") {
      return [
        {
          field: DOWN_PAYMENT_KEY,
          message: `Enter a down payment below your purchase price of ${formatWholeDollarForDisplay(price)}.`,
        },
      ];
    }
    return [];
  },
  render: (container: HTMLElement, values: WizardValues): void => {
    container.innerHTML = `
      <p class="wizard-step-desc">
        How much do you plan to put down? Enter 0 if you aren't putting any money down.
      </p>
      ${textFieldHtml(DOWN_PAYMENT_KEY, "Down payment", formatWholeDollarForDisplay(values.get(DOWN_PAYMENT_KEY)), {
        required: true,
        inputmode: "numeric",
      })}
      <p class="wizard-step-desc" data-derived-loan-amount aria-live="polite"></p>`;

    const summary = container.querySelector<HTMLElement>("[data-derived-loan-amount]");
    const renderDerived = (): void => {
      if (!summary) return;
      const derived = syncDerivedLoanAmount(values);
      summary.textContent =
        derived === ""
          ? ""
          : `Estimated loan amount: ${formatWholeDollarForDisplay(derived)}`;
    };

    const input = container.querySelector<HTMLInputElement>(`#${DOWN_PAYMENT_KEY}`);
    if (!input) return;
    bindMoneyInput(input, DOWN_PAYMENT_KEY, values);
    input.addEventListener("input", renderDerived);
    input.addEventListener("blur", renderDerived);
    renderDerived();
  },
};

/**
 * Step 4 — Stated credit. Optional with an explicit Skip, offering exactly the
 * authoritative TurboVets bands; a skipped step emits no credit value.
 */
export const purchaseCreditStep = creditStep;

/**
 * Step 5 — Military eligibility. The same optional application-level question,
 * prompt, Yes/No/Not sure answers, same-step component expansion, stale-clearing,
 * and Skip semantics as refinance, so the locked truth table is shared rather
 * than reimplemented.
 */
export const purchaseMilitaryStep = militaryStep;

/** Step 6 — Borrower name. Both parts required. */
export const purchaseNameStep = borrowerNameStep;

/** Step 7 — Borrower phone. Required, exactly ten digits once formatted. */
export const purchasePhoneStep = borrowerPhoneStep;

/**
 * Step 8 — Borrower email. Required and structurally valid; the single
 * deliberate submission from this step is delivered by WI-TV-W6-004.
 */
export const purchaseEmailStep = createBorrowerEmailStep(
  "We'll use this to follow up on your home purchase.",
);

/**
 * The locked purchase flow. Progress counts these input steps only: the
 * loan-purpose selector and the thank-you state are not numbered steps. Steps 4
 * and 5 (credit, military eligibility) are optional/skippable; every other step
 * up to the primary email is required.
 *
 * The shared optional co-borrower tail (WI-TV-W7-002) follows the primary email
 * step. Its decision and mailing steps are optional, and its three core data
 * steps exist only while the borrower has opted in, so a purchase application
 * without a co-borrower is numbered and submitted exactly as it was before.
 */
export function createPurchaseSteps(): WizardStep[] {
  return [
    homeSearchStep,
    purchasePriceStep,
    downPaymentStep,
    purchaseCreditStep,
    purchaseMilitaryStep,
    purchaseNameStep,
    purchasePhoneStep,
    purchaseEmailStep,
    ...createCoBorrowerSteps(),
  ];
}
